/**
 * Navigation Configuration
 *
 * Centralized configuration for application navigation menus.
 * Uses rule-based rendering for dynamic, permission-aware navigation.
 */

import {
  BarChart3,
  Package,
  Terminal,
  Building2,
  Users,
  Settings as SettingsIcon,
  Mail,
  Key,
  ShoppingBag,
  CreditCard,
  User,
  Rocket,
  Monitor,
  ScrollText,
  HardDrive,
  List,
  Webhook,
  Handshake,
  Radio,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { NavigationRule, BadgeConfig } from '../lib/navigation/rules/NavigationRule';
import {
  AlwaysVisibleRule,
  RequiresOrgRule,
  RequiresNoOrgRule,
  AdminOnlyRule,
  CompositeRule,
  RequiresPermissionRule,
  RequiresActiveSubscriptionRule,
  RequiresFeatureRule,
} from '../lib/navigation/rules';
import { logger } from '../lib/utils/logger';
import { APP_ROUTES, ADMIN_ROUTES } from './routes';

/**
 * Extended NavItem configuration with rule-based rendering.
 */
export interface NavItemConfig {
  /** Display label */
  label: string;

  /** Route path (omit for sections/categories) */
  path?: string;

  /** Lucide icon component */
  icon: LucideIcon;

  /** Nested navigation items (for expandable sections) */
  items?: NavItemConfig[];

  /**
   * Visibility rules - ALL must pass (AND logic) for item to appear.
   * If empty/undefined, item is always visible.
   * Use CompositeRule for OR logic.
   */
  visibilityRules?: NavigationRule[];

  /**
   * Clickability rules - ALL must pass for item to be clickable.
   * If undefined, item is clickable if visible.
   */
  clickableRules?: NavigationRule[];

  /**
   * Enabled rules - ALL must pass for item to be enabled.
   * If undefined, item is enabled if clickable.
   * Disabled items appear but are grayed out.
   */
  enabledRules?: NavigationRule[];

  /**
   * Badge configuration for counts/notifications.
   */
  badgeConfig?: BadgeConfig;
}

/**
 * Regular user navigation configuration.
 *
 * Rules applied:
 * - Most items require organization context
 * - User Management requires org:admin or org:owner role
 * - My Organizations always visible (works without org)
 * - Onboarding appears only when user has no organization
 */
export const REGULAR_NAV_CONFIG: NavItemConfig[] = [
  {
    label: 'Get Started',
    path: APP_ROUTES.ONBOARDING,
    icon: Rocket,
    visibilityRules: [new RequiresNoOrgRule()],
    badgeConfig: {
      variant: 'label',
      color: 'blue',
      text: 'New',
    },
  },
  {
    label: 'Dashboard',
    path: APP_ROUTES.DASHBOARD,
    icon: BarChart3,
    visibilityRules: [new RequiresOrgRule(), new RequiresActiveSubscriptionRule()],
  },
  {
    label: 'Task Library',
    path: APP_ROUTES.TASK_LIBRARY,
    icon: Package,
    visibilityRules: [new RequiresOrgRule(), new RequiresActiveSubscriptionRule()],
  },
  {
    label: 'Marketplace',
    path: APP_ROUTES.MARKETPLACE,
    icon: ShoppingBag,
    visibilityRules: [new RequiresOrgRule(), new RequiresActiveSubscriptionRule()],
    badgeConfig: {
      variant: 'label',
      color: 'blue',
      text: 'Beta',
    },
  },
  {
    label: 'Workflows',
    path: APP_ROUTES.WORKFLOWS,
    icon: Terminal,
    visibilityRules: [new RequiresOrgRule(), new RequiresActiveSubscriptionRule()],
  },
  {
    label: 'Deployments',
    path: APP_ROUTES.DEPLOYMENTS,
    icon: Radio,
    visibilityRules: [new RequiresOrgRule(), new RequiresActiveSubscriptionRule()],
  },
  {
    label: 'Storage',
    path: APP_ROUTES.STORAGE,
    icon: HardDrive,
    visibilityRules: [new RequiresOrgRule(), new RequiresActiveSubscriptionRule()],
  },
  {
    label: 'Settings',
    icon: SettingsIcon,
    visibilityRules: [new RequiresOrgRule()],
    items: [
      {
        label: 'Preferences',
        path: APP_ROUTES.SETTINGS_PROFILE,
        icon: User,
        visibilityRules: [new RequiresOrgRule()],
      },
      {
        label: 'This Organization',
        path: APP_ROUTES.SETTINGS_ORGANIZATION,
        icon: Building2,
        visibilityRules: [
          new CompositeRule(
            [new RequiresOrgRule(), new RequiresPermissionRule(['organizations:update'])],
            'AND',
          ),
        ],
      },
      {
        label: 'Team',
        path: APP_ROUTES.SETTINGS_TEAM,
        icon: Users,
        visibilityRules: [
          new CompositeRule(
            [
              new RequiresOrgRule(),
              new RequiresActiveSubscriptionRule(),
              new RequiresPermissionRule(['organizations:update']),
            ],
            'AND',
          ),
        ],
        badgeConfig: {
          fetchCount: async (orgId) => {
            if (!orgId) return 0;
            try {
              const { userApiClient } = await import('../lib/api/client');
              const response = await userApiClient.get<Array<{ id: string }>>(
                `/api/v1/organizations/${orgId}/memberships/approvals/pending`,
              );
              return response.length;
            } catch (error) {
              logger.error('Failed to fetch pending approvals count', error);
              return 0;
            }
          },
          variant: 'count',
          color: 'orange',
        },
      },
      {
        label: 'Billing',
        path: APP_ROUTES.SETTINGS_BILLING,
        icon: CreditCard,
        visibilityRules: [
          new CompositeRule(
            [new RequiresOrgRule(), new RequiresPermissionRule(['organizations:billing'])],
            'AND',
          ),
        ],
        badgeConfig: {
          variant: 'dot',
          color: 'orange',
          // Surface a nudge to billing admins when the org's subscription
          // isn't active — the only path back to feature access.
          getCount: (ctx) => (ctx.hasActiveSubscription() ? 0 : 1),
        },
      },
      {
        label: 'Webhooks',
        path: APP_ROUTES.SETTINGS_WEBHOOKS,
        icon: Webhook,
        visibilityRules: [
          new CompositeRule(
            [
              new RequiresOrgRule(),
              new RequiresActiveSubscriptionRule(),
              new RequiresFeatureRule('has_webhooks'),
            ],
            'AND',
          ),
        ],
      },
    ],
  },
  {
    label: 'Organizations',
    path: APP_ROUTES.ORGANIZATIONS,
    icon: Building2,
    visibilityRules: [new AlwaysVisibleRule()],
    // Future: Add badge for pending approvals
    // badgeConfig: {
    //   fetchCount: async () => {
    //     const orgs = await authService.getUserOrganizations(true);
    //     return orgs.pending.length;
    //   },
    //   color: 'orange',
    // },
  },
];

/**
 * Admin navigation configuration.
 *
 * Rules applied:
 * - All items require system:admin permission
 */
/**
 * Cached admin stats fetcher — shared by badge configs to avoid
 * separate API calls per nav item. Cache expires after 30 seconds.
 */
let adminStatsCache: { data: AdminStats; timestamp: number } | null = null;
const ADMIN_STATS_TTL = 30_000;

interface AdminStats {
  suspended_users_count: number;
  pending_invitations_count: number;
}

async function getAdminStats(): Promise<AdminStats> {
  if (adminStatsCache && Date.now() - adminStatsCache.timestamp < ADMIN_STATS_TTL) {
    return adminStatsCache.data;
  }
  const { adminApiClient } = await import('../lib/api/client');
  const data = await adminApiClient.get<AdminStats>('/api/v1/admin/stats');
  adminStatsCache = { data, timestamp: Date.now() };
  return data;
}

export const ADMIN_NAV_CONFIG: NavItemConfig[] = [
  {
    label: 'Dashboard',
    path: ADMIN_ROUTES.DASHBOARD,
    icon: BarChart3,
    visibilityRules: [new AdminOnlyRule()],
  },
  {
    label: 'Users',
    path: ADMIN_ROUTES.USERS,
    icon: Users,
    visibilityRules: [new AdminOnlyRule()],
    badgeConfig: {
      fetchCount: async () => {
        try {
          const stats = await getAdminStats();
          return stats.suspended_users_count;
        } catch (error) {
          logger.error('Failed to fetch suspended users count', error);
          return 0;
        }
      },
      variant: 'count',
      color: 'red',
    },
  },
  {
    label: 'Organizations',
    path: ADMIN_ROUTES.ORGANIZATIONS,
    icon: Building2,
    visibilityRules: [new AdminOnlyRule()],
  },
  {
    label: 'Subscriptions',
    path: ADMIN_ROUTES.SUBSCRIPTIONS,
    icon: CreditCard,
    visibilityRules: [new AdminOnlyRule()],
  },
  {
    label: 'Partners',
    path: ADMIN_ROUTES.PARTNERS,
    icon: Handshake,
    visibilityRules: [new AdminOnlyRule()],
  },
  {
    label: 'Invitations',
    path: ADMIN_ROUTES.INVITATIONS,
    icon: Mail,
    visibilityRules: [new AdminOnlyRule()],
    badgeConfig: {
      fetchCount: async () => {
        try {
          const stats = await getAdminStats();
          return stats.pending_invitations_count;
        } catch (error) {
          logger.error('Failed to fetch pending invitations count', error);
          return 0;
        }
      },
      variant: 'count',
      color: 'orange',
    },
  },
  {
    label: 'Roles',
    path: ADMIN_ROUTES.ROLES,
    icon: Key,
    visibilityRules: [new AdminOnlyRule()],
  },
  {
    label: 'Marketplace',
    path: ADMIN_ROUTES.MARKETPLACE,
    icon: ShoppingBag,
    visibilityRules: [new AdminOnlyRule()],
  },
  {
    label: 'Device Catalog',
    path: ADMIN_ROUTES.DRIVER_PACKS,
    icon: HardDrive,
    visibilityRules: [new AdminOnlyRule()],
  },
  {
    label: 'Logs',
    icon: ScrollText,
    visibilityRules: [new AdminOnlyRule()],
    items: [
      {
        label: 'Logs',
        path: ADMIN_ROUTES.LOGS_EVENTS,
        icon: ScrollText,
        visibilityRules: [new AdminOnlyRule()],
      },
      {
        label: 'Stripe Webhooks',
        path: ADMIN_ROUTES.LOGS_STRIPE_WEBHOOKS,
        icon: Webhook,
        visibilityRules: [new AdminOnlyRule()],
      },
    ],
  },
  {
    label: 'System Lists',
    path: ADMIN_ROUTES.SYSTEM_LISTS,
    icon: List,
    visibilityRules: [new AdminOnlyRule()],
  },
  {
    label: 'Settings',
    path: ADMIN_ROUTES.SETTINGS,
    icon: SettingsIcon,
    visibilityRules: [new AdminOnlyRule()],
  },
  {
    label: 'System Info',
    path: ADMIN_ROUTES.SYSTEM_INFO,
    icon: Monitor,
    visibilityRules: [new AdminOnlyRule()],
  },
];

/**
 * Helper function to create a nav item with common defaults.
 */
export function createNavItem(
  config: Partial<NavItemConfig> & Pick<NavItemConfig, 'label' | 'icon'>,
): NavItemConfig {
  return {
    visibilityRules: [new AlwaysVisibleRule()],
    ...config,
  };
}
